
import React, { useEffect, useMemo, useState } from "react";
import "../screenstyles/Product.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch, faEye } from "@fortawesome/free-solid-svg-icons";
import { Modal, Box, Typography, Button } from "@mui/material";
import {
  getAlwaseetOrders,
  type AlwaseetOrder,
} from "../services/alwaseetService";

const detailRowStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "180px 1fr",
  gap: "12px",
  padding: "10px 0",
  borderBottom: "1px solid #e5e7eb",
};

const labelStyle: React.CSSProperties = {
  fontWeight: 700,
  color: "#334155",
};

const valueStyle: React.CSSProperties = {
  color: "#0f172a",
  wordBreak: "break-word",
};

const formatCurrency = (value?: string | number) => {
  const amount = Number(value || 0);
  return `${amount.toLocaleString("en-US")} دينار`;
};

const badgeStyle = (status: string): React.CSSProperties => {
  let background = "#eef6f8";
  let color = "#3D7585";

  if (status.includes("تم التسليم")) {
    background = "#dcfce7";
    color = "#166534";
  } else if (status.includes("انتظار") || status.includes("مراجعة")) {
    background = "#fef3c7";
    color = "#92400e";
  } else if (status.includes("رفض") || status.includes("ملغي")) {
    background = "#fee2e2";
    color = "#991b1b";
  }

  return {
    display: "inline-block",
    padding: "6px 10px",
    borderRadius: "999px",
    background,
    color,
    fontSize: "12px",
    fontWeight: 700,
    whiteSpace: "nowrap",
  };
};

export default function AlwaseetOrders() {
  const [data, setData] = useState<AlwaseetOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedOrder, setSelectedOrder] = useState<AlwaseetOrder | null>(null);
  const [openModal, setOpenModal] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const result = await getAlwaseetOrders();
        console.log("Alwaseet Orders:", result);
        setData(Array.isArray(result) ? result : []);
      } catch (error) {
        console.error("Alwaseet Orders Error:", error);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  const filteredOrders = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();

    if (!q) return data;

    return data.filter((order) => {
      return (
        String(order.id || "").toLowerCase().includes(q) ||
        String(order.client_name || "").toLowerCase().includes(q) ||
        String(order.client_mobile || "").toLowerCase().includes(q) ||
        String(order.city_name || "").toLowerCase().includes(q) ||
        String(order.region_name || "").toLowerCase().includes(q) ||
        String(order.status || "").toLowerCase().includes(q) ||
        String(order.type_name || "").toLowerCase().includes(q)
      );
    });
  }, [data, searchQuery]);

  const handleOpenDetails = (order: AlwaseetOrder) => {
    setSelectedOrder(order);
    setOpenModal(true);
  };

  const handleCloseDetails = () => {
    setSelectedOrder(null);
    setOpenModal(false);
  };

  return (
    <div className="product-container">
      <div className="max-w-full flex items-center justify-between bg-[#262d34] text-white px-6 py-3 rounded-t-lg shadow-md">
        <h1
          className="text-xl flex-1"
          style={{ fontFamily: "Tajawal, sans-serif" }}
        >
          طــــلــــبــــات الــــوســــيــــط
        </h1>

        <div className="flex items-center bg-gray-100 rounded-full px-4 py-1 w-72 shadow-md ml-2">
          <FontAwesomeIcon icon={faSearch} className="text-gray-500 mr-2" />
          <input
            type="text"
            placeholder="البحث بالاسم أو الهاتف أو رقم الطلب..."
            className="bg-transparent focus:outline-none w-full px-4 text-sm text-gray-700 py-1 font-custom"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>

      {loading ? (
        <p>جاري تحميل البيانات...</p>
      ) : (
        <div className="table-container">
          <table className="product-table">
            <thead className="color-table">
              <tr>
                <th>#</th>
                <th>رقم الطلب</th>
                <th>اسم العميل</th>
                <th>الهاتف</th>
                <th>المدينة</th>
                <th>المنطقة</th>
                <th>نوع الشحنة</th>
                <th>عدد القطع</th>
                <th>سعر الطلب</th>
                <th>أجرة التوصيل</th>
                <th>الحالة</th>
                <th>التفاصيل</th>
              </tr>
            </thead>

            <tbody>
              {filteredOrders.map((order, index) => (
                <tr key={order.id}>
                  <td>{index + 1}</td>
                  <td>{order.id}</td>
                  <td>{order.client_name}</td>
                  <td>{order.client_mobile}</td>
                  <td>{order.city_name}</td>
                  <td>{order.region_name}</td>
                  <td>{order.type_name}</td>
                  <td>{order.items_number}</td>
                  <td>{formatCurrency(order.price)}</td>
                  <td>{formatCurrency(order.delivery_price)}</td>
                  <td>
                    <span style={badgeStyle(order.status || "")}>
                      {order.status || "غير متوفر"}
                    </span>
                  </td>
                  <td>
                    <div className="icon-container">
                      <button
                        className="icon-button"
                        onClick={() => handleOpenDetails(order)}
                      >
                        <FontAwesomeIcon icon={faEye} title="عرض التفاصيل" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal open={openModal} onClose={handleCloseDetails}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "min(900px, 95vw)",
            maxHeight: "90vh",
            overflowY: "auto",
            bgcolor: "white",
            borderRadius: "12px",
            boxShadow: 24,
            p: 3,
            direction: "rtl",
          }}
        >
          <Typography
            variant="h6"
            component="h2"
            sx={{
              textAlign: "center",
              marginBottom: 2,
              fontFamily: "Tajawal, sans-serif",
              fontWeight: 700,
            }}
          >
            تفاصيل طلب الوسيط
          </Typography>

          {selectedOrder && (
            <div>
              <div style={detailRowStyle}>
                <div style={labelStyle}>رقم الطلب</div>
                <div style={valueStyle}>{selectedOrder.id}</div>
              </div>

              <div style={detailRowStyle}>
                <div style={labelStyle}>اسم العميل</div>
                <div style={valueStyle}>{selectedOrder.client_name}</div>
              </div>

              <div style={detailRowStyle}>
                <div style={labelStyle}>الهاتف الأول</div>
                <div style={valueStyle}>{selectedOrder.client_mobile}</div>
              </div>

              <div style={detailRowStyle}>
                <div style={labelStyle}>الهاتف الثاني</div>
                <div style={valueStyle}>
                  {selectedOrder.client_mobile2 || "غير متوفر"}
                </div>
              </div>

              <div style={detailRowStyle}>
                <div style={labelStyle}>المدينة</div>
                <div style={valueStyle}>{selectedOrder.city_name}</div>
              </div>

              <div style={detailRowStyle}>
                <div style={labelStyle}>المنطقة</div>
                <div style={valueStyle}>{selectedOrder.region_name}</div>
              </div>

              <div style={detailRowStyle}>
                <div style={labelStyle}>الموقع</div>
                <div style={valueStyle}>
                  {selectedOrder.location || "غير متوفر"}
                </div>
              </div>

              <div style={detailRowStyle}>
                <div style={labelStyle}>نوع الشحنة</div>
                <div style={valueStyle}>{selectedOrder.type_name}</div>
              </div>

              <div style={detailRowStyle}>
                <div style={labelStyle}>عدد القطع</div>
                <div style={valueStyle}>{selectedOrder.items_number}</div>
              </div>

              <div style={detailRowStyle}>
                <div style={labelStyle}>حجم الطرد</div>
                <div style={valueStyle}>{selectedOrder.package_size}</div>
              </div>

              <div style={detailRowStyle}>
                <div style={labelStyle}>سعر الطلب</div>
                <div style={valueStyle}>{formatCurrency(selectedOrder.price)}</div>
              </div>

              <div style={detailRowStyle}>
                <div style={labelStyle}>أجرة التوصيل</div>
                <div style={valueStyle}>
                  {formatCurrency(selectedOrder.delivery_price)}
                </div>
              </div>

              <div style={detailRowStyle}>
                <div style={labelStyle}>الحالة</div>
                <div style={valueStyle}>{selectedOrder.status}</div>
              </div>

              <div style={detailRowStyle}>
                <div style={labelStyle}>استبدال</div>
                <div style={valueStyle}>
                  {selectedOrder.replacement === "1" ? "نعم" : "لا"}
                </div>
              </div>

              <div style={detailRowStyle}>
                <div style={labelStyle}>ملاحظات التاجر</div>
                <div style={valueStyle}>
                  {selectedOrder.merchant_notes || "لا توجد"}
                </div>
              </div>

              <div style={detailRowStyle}>
                <div style={labelStyle}>ملاحظات المشكلة</div>
                <div style={valueStyle}>
                  {selectedOrder.issue_notes || "لا توجد"}
                </div>
              </div>

              <div style={detailRowStyle}>
                <div style={labelStyle}>تاريخ الإنشاء</div>
                <div style={valueStyle}>{selectedOrder.created_at}</div>
              </div>

              <div style={detailRowStyle}>
                <div style={labelStyle}>آخر تحديث</div>
                <div style={valueStyle}>{selectedOrder.updated_at}</div>
              </div>

              <div style={detailRowStyle}>
                <div style={labelStyle}>رقم الفاتورة</div>
                <div style={valueStyle}>
                  {selectedOrder.merchant_invoice_id || "غير متوفر"}
                </div>
              </div>

              <div style={detailRowStyle}>
                <div style={labelStyle}>مؤكد ماليًا</div>
                <div style={valueStyle}>
                  {selectedOrder.deliver_confirmed_fin === "1" ? "نعم" : "لا"}
                </div>
              </div>

              <div style={detailRowStyle}>
                <div style={labelStyle}>داخل سجل فاتورة</div>
                <div style={valueStyle}>
                  {selectedOrder.has_merchant_fin_record === "1" ? "نعم" : "لا"}
                </div>
              </div>

              <div style={detailRowStyle}>
                <div style={labelStyle}>تسليم عاجل</div>
                <div style={valueStyle}>
                  {selectedOrder.urgent === "1" ? "نعم" : "لا"}
                </div>
              </div>

              <div style={detailRowStyle}>
                <div style={labelStyle}>موعد التسليم المتوقع</div>
                <div style={valueStyle}>
                  {selectedOrder.deliver_estimation_datetime || "غير متوفر"}
                </div>
              </div>

              <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
                <Button
                  variant="contained"
                  onClick={handleCloseDetails}
                  sx={{
                    backgroundColor: "#262d34",
                    color: "#fff",
                    fontFamily: "Tajawal, sans-serif",
                    "&:hover": {
                      backgroundColor: "#1a1f24",
                    },
                  }}
                >
                  إغلاق
                </Button>
              </Box>
            </div>
          )}
        </Box>
      </Modal>
    </div>
  );
}